import { VIDEO_PLAYBACK, VIDEO_POSTER } from '@guildite/domain/video';

// Shrinks photos before upload so sellers on mobile data send less: longest
// side 1600 px, JPEG at 85%. Falls back to the original file if the browser
// cannot decode it.
const MAX_SIDE = 1600;

export async function prepareImage(file: File): Promise<Blob> {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type) || typeof createImageBitmap !== 'function') return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 1_500_000) return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

// Cloudinary delivers a right-sized, modern format for each screen
// ("f_auto,q_auto,w_…", PLAN section 3). Other URLs pass through.
export function sizedImage(url: string, width: number): string {
  const marker = '/image/upload/';
  if (!url.includes('res.cloudinary.com') || !url.includes(marker)) return url;
  const [before, after = ''] = url.split(marker);
  if (/^[a-z]_[^/]+\//.test(after)) return url;
  return `${before}${marker}f_auto,q_auto,c_limit,w_${Math.round(width)}/${after}`;
}

// A Cloudinary video as an MP4 (H.264) that plays on Android, iPhone and
// desktop, at the quality Cloudinary picks. Videos over 40 MB get exactly
// this copy made right after upload (DECISIONS "Videos: no time limit, up to
// 100 MB"); smaller ones are converted on the first view. Other URLs pass
// through.
export function playableVideo(url: string): string {
  const marker = '/video/upload/';
  if (!url.includes('res.cloudinary.com') || !url.includes(marker)) return url;
  const [before, after = ''] = url.split(marker);
  if (/^[a-z]{1,3}_[^/]+\//.test(after)) return url;
  return `${before}${marker}${VIDEO_PLAYBACK.transformation}/${after.replace(/\.[A-Za-z0-9]+$/, '')}.${VIDEO_PLAYBACK.format}`;
}

// A still from the start of a Cloudinary video (720 px wide at most), so a
// feed can show the clip without loading it. One size for every screen, so
// it can be made ahead for big videos. Null for other URLs.
export function videoPoster(url: string): string | null {
  const marker = '/video/upload/';
  if (!url.includes('res.cloudinary.com') || !url.includes(marker)) return null;
  const [before, after = ''] = url.split(marker);
  return `${before}${marker}${VIDEO_POSTER.transformation}/${after.replace(/\.[A-Za-z0-9]+$/, '')}.${VIDEO_POSTER.format}`;
}

// A video's cover picture (DECISIONS, 22 October): the frame at `fraction`
// of the clip (0 is the first frame), drawn on this device from the chosen
// file as a JPEG no bigger than a photo upload.
export function captureVideoFrame(file: Blob, fraction: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    const timer = window.setTimeout(() => finish(new Error('timeout')), 10000);
    function finish(error: Error | null, blob?: Blob | null) {
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      if (error || !blob) reject(error ?? new Error('no frame'));
      else resolve(blob);
    }
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onerror = () => finish(new Error('unreadable'));
    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      // A hair past 0 so browsers that skip seeking to 0 still draw a frame.
      video.currentTime = Math.min(Math.max(duration * Math.min(Math.max(fraction, 0), 1), 0.05), Math.max(duration - 0.05, 0.05));
    };
    video.onseeked = () => {
      const scale = Math.min(1, MAX_SIDE / Math.max(video.videoWidth, video.videoHeight, 1));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
      const context = canvas.getContext('2d');
      if (!context || !video.videoWidth) return finish(new Error('no frame'));
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => finish(null, blob), 'image/jpeg', 0.85);
    };
    video.src = url;
  });
}

// Photo and video shapes (DECISIONS "Photo and video shapes"), like
// Instagram: a gallery or chat bubble takes the shape of its first photo or
// video, between tall 4:5 and wide 1.91:1; anything else fits inside it
// whole. Shapes seen on this device are remembered for the visit, so a
// gallery opened again doesn't change size while it loads.
export const TALLEST = 4 / 5;
export const WIDEST = 1.91;

export function clampRatio(width: number, height: number): number | null {
  if (!(width > 0) || !(height > 0)) return null;
  return Math.min(WIDEST, Math.max(TALLEST, width / height));
}

const seenRatios = new Map<string, number>();

export function knownRatio(url: string | null | undefined): number | null {
  return url ? seenRatios.get(url) ?? null : null;
}

export function rememberRatio(url: string, width: number, height: number): number | null {
  const ratio = clampRatio(width, height);
  if (ratio) seenRatios.set(url, ratio);
  return ratio;
}
